import { OnChangeFn, SortingState } from "@tanstack/react-table";
import {
  transformColumnIdToCustomFieldName,
  transformCustomFieldNameToColumnId,
} from "@utils";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const paramName = "sort";

function parseSorting(searchParams: URLSearchParams): SortingState {
  const sortBy = searchParams.get(paramName);

  return (
    sortBy?.split(",").map((field) => {
      const [id, order] = field.split("|");
      return {
        id: transformCustomFieldNameToColumnId(id),
        desc: order === "desc",
      };
    }) ?? []
  );
}

export function useTableSort(): [SortingState, OnChangeFn<SortingState>] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortingState, setSortingState] = useState<SortingState>(() =>
    parseSorting(searchParams),
  );

  useEffect(() => {
    setSortingState(parseSorting(searchParams));
  }, [searchParams]);

  function onSortChange(
    updaterOrValue: SortingState | ((old: SortingState) => SortingState),
  ) {
    const newSortingState =
      typeof updaterOrValue === "function"
        ? updaterOrValue(sortingState)
        : updaterOrValue;

    const sortBy = newSortingState
      .map((sort) => {
        const sortFieldName = transformColumnIdToCustomFieldName(sort.id);
        return `${sortFieldName}|${sort.desc ? "desc" : "asc"}`;
      })
      .join(",");

    const newParams = new URLSearchParams(searchParams);
    if (sortBy) {
      newParams.set(paramName, sortBy);
    } else {
      newParams.delete(paramName);
    }

    setSearchParams(newParams);
  }

  return [sortingState, onSortChange];
}
